import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { captureRevision, setExpectedRevision } from 'core/api/revisionStore';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { useNavigate } from 'react-router-dom';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { ImportDialog } from 'features/workbooks/ImportDialog';
import { NewQuestionsImportDialog } from 'features/workbooks/NewQuestionsImportDialog';
import { invalidateQuestionnaireQueries, saveBlobFile } from 'features/workbooks/workbook';
import { Button } from 'shared/components/Button';
import { Card } from 'shared/components/Feedback';
import { useToast } from 'shared/components/Toast';

export function QuestionnaireWorkbookCard() {
  const { t } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canExport = hasPermission(PERMISSIONS.questionnaireExport);
  const canImport = hasPermission(PERMISSIONS.questionnaireImport);
  const canDownloadNewQuestions = hasPermission(PERMISSIONS.questionnaireExport);
  const canImportNewQuestions = hasPermission(PERMISSIONS.questionnaireImport);
  const [pending, setPending] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [newQuestionsOpen, setNewQuestionsOpen] = useState(false);
  const importButtonRef = useRef(null);
  const newQuestionsButtonRef = useRef(null);
  const inflightRef = useRef(false);

  if (!canExport && !canImport && !canDownloadNewQuestions && !canImportNewQuestions) {
    return null;
  }

  const download = async (key, runner, { successKey, errorKey } = {}) => {
    if (inflightRef.current) {
      return;
    }
    inflightRef.current = true;
    setPending(key);
    setStatusMessage(t(successKey === 'workbook.newQuestions.downloadSuccess'
      ? 'workbook.newQuestions.downloadBusy'
      : 'workbook.status.working'));
    try {
      const result = await runner();
      saveBlobFile(result.blob, result.filename);
      if (successKey) {
        toast.success(t(successKey));
        setStatusMessage(t(successKey));
      } else {
        setStatusMessage('');
      }
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        return;
      }
      if (error instanceof ApiError && error.isForbidden()) {
        toast.error(t('errors.forbidden'));
        setStatusMessage(t('errors.forbidden'));
        if (key !== 'new-template') {
          navigate(paths.forbidden);
        }
        return;
      }
      toast.error(humanizeApiError(error, t) || t(errorKey || 'workbook.errors.downloadFailed'));
      setStatusMessage(humanizeApiError(error, t) || t(errorKey || 'workbook.errors.downloadFailed'));
    } finally {
      inflightRef.current = false;
      setPending(null);
    }
  };

  return (
    <Card className="workbook-card" id="workbook">
      <div className="sr-only" aria-live="polite">{statusMessage}</div>
      <h2>{t('workbook.questionnaireTitle')}</h2>

      {(canExport || canImport) ? (
        <section className="workbook-card-section" aria-labelledby="workbook-existing-heading">
          <h3 id="workbook-existing-heading">{t('workbook.existingManagementTitle')}</h3>
          <p className="muted">{t('workbook.fullImportHint')}</p>
          <p className="muted">{t('workbook.fullExportHint')}</p>
          <p className="muted">{t('workbook.previewHint')}</p>
          <div className="toolbar wrap workbook-card-actions">
            {canExport ? (
              <Button
                tone="secondary"
                loading={pending === 'export'}
                disabled={Boolean(pending)}
                onClick={() => download('export', () => assessmentApi.exportQuestionnaire())}
              >
                {t('workbook.exportQuestionnaire')}
              </Button>
            ) : null}
            {canImport ? (
              <Button
                ref={importButtonRef}
                onClick={() => setImportOpen(true)}
                disabled={Boolean(pending)}
              >
                {t('workbook.importQuestionnaire')}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}

      {(canDownloadNewQuestions || canImportNewQuestions) ? (
        <section className="workbook-card-section" aria-labelledby="workbook-new-questions-heading">
          <h3 id="workbook-new-questions-heading">{t('workbook.newQuestions.title')}</h3>
          <p className="muted">{t('workbook.newQuestions.vsFullImport')}</p>
          <p className="muted">{t('workbook.newQuestions.addOnlyHint')}</p>
          <ul className="workbook-hint-list">
            <li>{t('workbook.newQuestions.explanationSelectSection')}</li>
            <li>{t('workbook.newQuestions.explanationTwoAnswers')}</li>
            <li>{t('workbook.newQuestions.explanationWeights')}</li>
            <li>{t('workbook.newQuestions.explanationAppend')}</li>
          </ul>
          <div className="toolbar wrap workbook-card-actions" data-testid="workbook-new-questions-actions">
            {canDownloadNewQuestions ? (
              <Button
                tone="secondary"
                loading={pending === 'new-template'}
                disabled={Boolean(pending)}
                onClick={() => download(
                  'new-template',
                  () => assessmentApi.downloadNewQuestionsTemplate(),
                  {
                    successKey: 'workbook.newQuestions.downloadSuccess',
                    errorKey: 'workbook.errors.downloadFailed',
                  },
                )}
              >
                {t('workbook.newQuestions.downloadTemplate')}
              </Button>
            ) : null}
            {canImportNewQuestions ? (
              <Button
                ref={newQuestionsButtonRef}
                onClick={() => setNewQuestionsOpen(true)}
                disabled={Boolean(pending)}
              >
                {t('workbook.newQuestions.import')}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        restoreFocusRef={importButtonRef}
        kind="questionnaire"
        explanations={[
          'workbook.sync.immutableKeys',
          'workbook.sync.newKeys',
          'workbook.sync.numericZero',
          'workbook.sync.blankWeight',
          'workbook.sync.newPatternWeights',
          'workbook.sync.noSilentDelete',
        ]}
        previewRequest={(file, config) => assessmentApi.previewQuestionnaireImport(file, config)}
        commitRequest={(file, payload, config) => assessmentApi.commitQuestionnaireImport(file, payload, config)}
        downloadLatest={() => assessmentApi.exportQuestionnaire()}
        successAction={{ labelKey: 'nav.builder', path: paths.builder }}
        onSuccess={async (completed) => {
          if (completed.revision) {
            setExpectedRevision(completed.revision);
          }
          captureRevision(completed);
          await assessmentApi.overview();
          await invalidateQuestionnaireQueries(queryClient);
          toast.success(t('workbook.success'));
        }}
      />
      <NewQuestionsImportDialog
        open={newQuestionsOpen}
        onClose={() => setNewQuestionsOpen(false)}
        restoreFocusRef={newQuestionsButtonRef}
        canImport={canImportNewQuestions}
        onSuccess={async (completed) => {
          if (completed.revision) {
            setExpectedRevision(completed.revision);
          }
          captureRevision(completed);
          await assessmentApi.overview();
          toast.success(t('workbook.newQuestions.success', {
            questions: completed.questionsCount,
            answers: completed.answersCount,
          }));
        }}
      />
    </Card>
  );
}
